<script setup lang="ts">
import { computed, inject, watch, ref, onMounted, onBeforeUnmount, nextTick, provide, useTemplateRef } from 'vue';
import type { FieldSchema, FieldSettings } from '../../../types';
import { FORM_DEBOUNCE } from '@Constants';
import { getMicrochipIssuer } from '@AnimalModule';
import { useForm } from '../../../composables';

const emits = defineEmits<{
  onFormSubmit: [];
  onFieldUpdate: [FieldSchema];
}>();

const props = withDefaults(
  defineProps<{
    editRuleByPermission?: boolean;
    fieldLoading?: boolean;
    formError?: string | boolean;
    granularValidation?: boolean;
    hasInlineEdit?: boolean;
    hasRenderMode?: boolean;
    hybridEditMode?: boolean;
    isInline?: boolean;
    isOnEditMode?: boolean;
    isOnODetails?: boolean;
    loading?: boolean;
    loadingTemplate?: number;
    schema: FieldSchema[];
  }>(),
  {
    editRuleByPermission: true,
    granularValidation: true,
    isOnODetails: false,
  }
);

const { autoPopulateMicrochipIssuer, granularFieldValidation } = useForm();

let inlineEditOriginalModel: any | undefined = undefined;

const fieldOnInlineEdit = defineModel('fieldOnInlineEdit', { default: '' });
const isInlineEditing = defineModel('isInlineEditing', { default: false });

const formContainerRef = useTemplateRef<HTMLElement>('formContainer');

const isInitialMicrochipLoad = ref(true);
const isSmallContainer = ref(false);
let resizeObserver: ResizeObserver | null = null;

const hasFormErrorTargetFromInject = inject<boolean>('formHasErrorTarget', true);

const hasFormErrorTargetInDom = ref(false);

const hasFormErrorTarget = computed(() => {
  if (hasFormErrorTargetFromInject) return true;
  return hasFormErrorTargetInDom.value;
});

const genderModel = computed(() => props.schema.find(({ name }) => name === 'gender')?.model);
const hasErrors = computed(() => props.schema.some((field) => field.state.errors.size));
const microchipModel = computed(() => props.schema.find(({ name }) => name === 'microchip')?.model);
const hasMicrochipFields = computed(
  () =>
    props.schema.some(({ name }) => name === 'microchip') &&
    props.schema.some(({ name }) => name === 'microchip_issuer')
);

provide('animalGenderModel', genderModel);

onMounted(() => nextTick(() => setupObserver()));

onBeforeUnmount(() => {
  disconnectObserver();
});

watch(fieldOnInlineEdit, () => (isInlineEditing.value = !!fieldOnInlineEdit.value));
watch(microchipModel, (newMicrochip) => onNewMicrochip(newMicrochip));
watch(
  () => formContainerRef.value,
  async () => await callObserver()
);

function onNewMicrochip(newMicrochip: string | null) {
  if (isInitialMicrochipLoad.value) return (isInitialMicrochipLoad.value = false);

  onMicrochipChange(newMicrochip);
}

function setupObserver() {
  disconnectObserver();

  if (!formContainerRef.value) return;

  resizeObserver = new ResizeObserver((entries) => {
    if (!entries[0]) return;
    isSmallContainer.value = entries[0].contentRect.width < 300;
  });

  resizeObserver.observe(formContainerRef.value);
}

function disconnectObserver() {
  if (!resizeObserver) return;

  resizeObserver.disconnect();
  resizeObserver = null;
}

async function callObserver() {
  await nextTick();
  setupObserver();
}

const errorMessage = computed(() => {
  const hasSpecificError = typeof props.formError === 'string' && !!props.formError;

  if (!hasErrors.value && !hasSpecificError) return;

  const isDefaultMessage = props.formError && typeof props.formError === 'boolean';
  if (isDefaultMessage) return 'components.forms.errors.reviewForm';

  return props.formError || '';
});

watch(
  () => !!errorMessage.value,
  (hasError) => {
    if (hasError && !hasFormErrorTargetFromInject && !hasFormErrorTargetInDom.value) {
      nextTick(() => {
        if (typeof document !== 'undefined' && document.getElementById('formErrorMessage')) {
          hasFormErrorTargetInDom.value = true;
        }
      });
    }
  }
);

function onSubmitForm() {
  setTimeout(() => {
    emits('onFormSubmit');
  }, FORM_DEBOUNCE);
}

// NOTE : Returns true so we can use short circuit in the template then avoidind aa extra event emit
function onEditFieldCancel(): true {
  fieldOnInlineEdit.value = '';
  return true;
}

function onEditFieldClick(name: string, model: any, settings: FieldSettings) {
  if (!props.hasInlineEdit) return;
  if (!settings.editable) return;
  if (!props.editRuleByPermission) return;

  if (fieldOnInlineEdit.value) {
    props.schema.forEach((field) => {
      if (field.name !== fieldOnInlineEdit.value) return;

      field.model = inlineEditOriginalModel;
    });
  }

  fieldOnInlineEdit.value = name;
  inlineEditOriginalModel = model;
}

function onEditFieldRequest(field: FieldSchema) {
  emits('onFieldUpdate', field);
  fieldOnInlineEdit.value = '';
  inlineEditOriginalModel = undefined;
}

function onMicrochipChange(newMicrochip: string | null) {
  if (!hasMicrochipFields.value || !newMicrochip) return;

  autoPopulateMicrochipIssuer(props.schema, newMicrochip, getMicrochipIssuer);
}
</script>

<template>
  <AFormRenderSkeleton
    v-if="loading"
    :loadingTemplate
  />

  <form
    v-else
    ref="formContainer"
    @submit.prevent="onSubmitForm"
  >
    <OFieldset
      v-for="(field, index) in schema"
      v-model="field.model"
      class="mb-6 last:mb-0"
      :key="field.name ?? index"
      :editRuleByPermission
      :error="[...field.state.errors][0]"
      :field
      :fieldLoading
      :fieldOnInlineEdit
      :hasInlineEdit
      :hasRenderMode
      :hybridEditMode
      :isInline
      :isInlineEditing
      :isOnEditMode
      :isOnODetails
      :isSmallContainer
      :loading
      @onEditFieldCancel="onEditFieldCancel() && (field.model = inlineEditOriginalModel)"
      @onEditFieldClick="onEditFieldClick(field.name, field.model, field.settings)"
      @onEditFieldRequest="onEditFieldRequest(field)"
      @onModelUpdate="granularValidation && granularFieldValidation(field)"
      @onMultiSelectBlur="field.state.blur = true"
      @onMultiSelectFocus="field.state.blur = false"
    />

    <template v-if="errorMessage">
      <Teleport
        v-if="hasFormErrorTarget"
        defer
        to="#formErrorMessage"
      >
        <Message
          class="!mx-0 !mb-3 !mt-0 h-fit overflow-hidden"
          pt:text="text-xs"
          pt:wrapper="h-fit"
          severity="error"
          :closable="false"
        >
          <p class="break-words">{{ $t(errorMessage) }}</p>
        </Message>
      </Teleport>
    </template>

    <slot />
  </form>
</template>
